import { useEffect, useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, ObjectSchema, string } from 'yup';

import { CompanyReadModel, handleBackendValidationErrors } from '@client/api';
import {
  Button,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

type FormModel = Omit<
  CompanyReadModel,
  | 'invoicingAccount'
  | 'source'
  | 'country'
  | 'status'
  | 'personCount'
  | 'agreementCount'
  | 'stakeholderId'
  | 'id'
  | 'hasAgreement'
  | 'roles'
  | 'organizationNumber'
  | 'organizationForm'
  | 'industryCode'
  | 'institutionalSectorCode'
  | 'numberOfEmployees'
  | 'isBankrupt'
  | 'isUnderLiquidation'
  | 'isUnderForcedDissolution'
  | 'parentOrganizationNumber'
  | 'registeredInVatRegistry'
  | 'vatRegistrationDate'
  | 'lastAnnualAccountsYear'
  | 'activity'
  | 'remarks'
  | 'brregUrl'
  | 'website'
  | 'emailAddress'
  | 'mobilePhone'
  | 'entityRegistrationDate'
  | 'isPartOfGroup'
  | 'languageCode'
>;

const schema: ObjectSchema<FormModel> = object({
  name: string().required(),
  vatId: string().nullable(),
  businessAddress: object({
    address: string().nullable(),
    postalCode: string().nullable(),
    city: string().nullable(),
    municipality: string().nullable(),
  }).nullable(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type CompanyFormData = FormValues;

interface CompanyFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  company?: CompanyReadModel;
  open?: boolean;
}

export function CompanyForm({ open = true, title, onSubmit, onClose, company }: CompanyFormProps) {
  const { t: tCommon } = useTranslation();
  const { t: tCompanies } = useTranslation('companies');

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: company?.name ?? '',
      vatId: company?.vatId ?? '',
      businessAddress: company?.businessAddress ?? {
        address: null,
        postalCode: null,
        city: null,
        municipality: null,
      },
    }),
    [company?.name, company?.vatId, company?.businessAddress],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset, setError } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit(data).catch((error) => {
      handleBackendValidationErrors(
        {
          Name: 'name',
          VatId: 'vatId',
        },
        error,
        setError,
      );
    });
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={open} appear size="lg" onClose={onClose} title={title}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 p-4">
            <article>
              <GridRow cols={{ def: 1, sm: 6 }} spacingX={2} spacingY={4}>
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField<FormValues, FormPath>
                    name="name"
                    label={tCompanies('searchModal.name')}
                    required
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>

                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField<FormValues, FormPath>
                    name="vatId"
                    label={tCompanies('searchModal.orgNumber')}
                    required
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField<FormValues, FormPath>
                    name="businessAddress.address"
                    label={tCompanies('form.address')}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
                <GridCol span={{ def: 1, sm: 3 }}>
                  <FormField<FormValues, FormPath>
                    name="businessAddress.postalCode"
                    label={tCompanies('form.postalCode')}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
                <GridCol span={{ def: 1, sm: 3 }}>
                  <FormField<FormValues, FormPath>
                    name="businessAddress.city"
                    label={tCompanies('form.postalCity')}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
                <GridCol span={{ def: 1, sm: 3 }}>
                  <FormField<FormValues, FormPath>
                    name="businessAddress.municipality"
                    label={tCompanies('form.municipality')}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
              </GridRow>
            </article>
          </ModalContent>

          <ModalFooter>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('back')}
            </Button>
            <FormSubmitButton type="submit">{tCommon('save')}</FormSubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
